import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { questionApi, learningApi, type QuestionItem } from '../../api/client';
import { Icon } from '../../components/Icon';

const TYPE_LABEL: Record<string, string> = {
  single: '单选题', multi: '多选题', judge: '判断题', deep: '深度知识题',
};
const TYPE_COLOR: Record<string, string> = {
  single: '#3b82f6', multi: '#8b5cf6', judge: '#10b981', deep: '#f59e0b',
};

function answerIndex(value: unknown): number | null {
  if (typeof value === 'number' && Number.isInteger(value)) return value;
  if (typeof value !== 'string') return null;
  const text = value.trim().toUpperCase();
  const letter = text.match(/^([A-F])(?:[.、)\s:]|$)/);
  if (letter) return letter[1].charCodeAt(0) - 'A'.charCodeAt(0);
  const numeric = Number(text);
  return Number.isInteger(numeric) ? numeric : null;
}

function normalizeAnswer(value: unknown): number[] {
  const values = Array.isArray(value) ? value : (value == null ? [] : [value]);
  return values
    .map(answerIndex)
    .filter((item): item is number => item !== null)
    .sort((a, b) => a - b);
}

function localGrade(type: string, userAnswer: number[], correctAnswer: unknown) {
  if (type === 'deep') return { isCorrect: false, score: 0 };
  const user = [...userAnswer].sort((a, b) => a - b);
  const correct = normalizeAnswer(correctAnswer);
  const exact = correct.length > 0
    && user.length === correct.length
    && user.every((value, index) => value === correct[index]);
  if (exact) return { isCorrect: true, score: 100 };
  if (type === 'multi' && user.length > 0 && user.every(value => correct.includes(value))) {
    return { isCorrect: false, score: 50 };
  }
  return { isCorrect: false, score: 0 };
}

function serverCorrectFlag(result: Record<string, unknown>): boolean | null {
  const value = result.is_correct ?? result.isCorrect;
  if (value === true || value === 1 || value === '1' || value === 'true') return true;
  if (value === false || value === 0 || value === '0' || value === 'false') return false;
  return null;
}

export function SingleQuizPage() {
  const params = useParams<{ cardNo?: string; publicId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { cardNo, publicId } = params;

  // 错题本连续练习模式
  const wrongMode = searchParams.get('mode') === 'wrong';
  const wrongIds = searchParams.get('ids')?.split(',').filter(Boolean) || [];
  const wrongIdx = parseInt(searchParams.get('idx') || '0', 10) || 0;
  const deckId = searchParams.get('deck_id') || undefined;

  const [question, setQuestion] = useState<QuestionItem | null>(null);
  const [loading, setLoading] = useState(true);
  // 保留错误对象以区分类型：40303=企业定制题无权限（DECK_FORBIDDEN），其余按“题目不存在”提示
  const [error, setError] = useState<{ message: string; forbidden: boolean } | null>(null);
  const [selected, setSelected] = useState<number[]>([]);
  const [submitted, setSubmitted] = useState(false);
  const [score, setScore] = useState(0);
  const [answerIsCorrect, setAnswerIsCorrect] = useState<boolean | null>(null);
  const [showExplanation, setShowExplanation] = useState(false);
  const [correctAnswer, setCorrectAnswer] = useState<number[]>([]);
  const [serverExplanation, setServerExplanation] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // #75 本题开始作答的时间戳（题目渲染出来才起算），提交时换算成 used_seconds 上报。
  // 用 ref 而非 state：仅在提交时读取，不需要触发重渲染。
  const startedAtRef = useRef(0);

  useEffect(() => {
    setLoading(true);
    setError(null);
    setSelected([]);
    setSubmitted(false);
    setScore(0);
    setAnswerIsCorrect(null);
    setShowExplanation(false);
    setCorrectAnswer([]);
    setServerExplanation('');
    setSubmitting(false);
    setSubmitError('');
    startedAtRef.current = 0;

    // QR token 是十六进制字符串，不能 parseInt；纯数字才走卡号逻辑
    let fetchPromise: Promise<QuestionItem | null>;
    if (publicId) {
      fetchPromise = questionApi.getByPublicId(publicId, wrongMode ? undefined : deckId);
    } else if (cardNo) {
      // 始终用字符串传给 /q/<card_no>，后端支持 token / public_id / 数字序号
      fetchPromise = questionApi.getByCardNo(cardNo);
    } else {
      fetchPromise = Promise.reject(new Error('缺少题目标识'));
    }

    fetchPromise
      .then(q => {
        if (q) {
          setQuestion(q);
          // 题目取到即开始计时（#75 累计时长）
          startedAtRef.current = Date.now();
          // 套题模式：进入第一题时重置/初始化本次成绩累积（供结算页展示）
          if (!wrongMode && q.deck_id) {
            const key = `kc_deck_${q.deck_id}`;
            try {
              const raw = sessionStorage.getItem(key);
              if (!raw || (q.position ?? 0) === 1) {
                sessionStorage.setItem(key, JSON.stringify({
                  title: q.deck_title || '',
                  total: q.total_in_deck || 0,
                  answered: 0,
                  correct: 0,
                  deep: 0,
                }));
              }
            } catch { /* ignore */ }
          }
          try { learningApi.recordView(q.public_id); } catch { /* ignore */ }
        } else setError({ message: '未找到该题目', forbidden: false });
      })
      .catch(e => {
        const tag = (e as Error & { tag?: string })?.tag || '';
        const forbidden = tag === 'DECK_FORBIDDEN';
        setError({
          message: e instanceof Error ? e.message : '加载失败',
          forbidden,
        });
      })
      .finally(() => setLoading(false));
  }, [cardNo, publicId, deckId, wrongMode]);

  function toggleOption(i: number) {
    if (submitted || !question) return;
    if (question.type === 'multi') {
      setSelected(prev => prev.includes(i) ? prev.filter(x => x !== i) : [...prev, i]);
    } else {
      setSelected([i]);
    }
  }

  function isCorrectOption(i: number): boolean {
    if (!question) return false;
    const rawAns = correctAnswer.length > 0
      ? correctAnswer
      : normalizeAnswer(question.answer);
    return rawAns.includes(i);
  }

  async function handleSubmit(answer = selected) {
    if (!question || (answer.length === 0 && question.type !== 'deep') || submitting) return;
    setSubmitting(true);
    setSubmitError('');
    try {
      const legacyGrade = localGrade(question.type, answer, question.answer);
      // #75 本题耗时：上限 30 分钟，避免挂着页面不动（切后台/隔天回来）把累计时长撑爆。
      const usedSeconds = startedAtRef.current
        ? Math.min(1800, Math.max(0, Math.round((Date.now() - startedAtRef.current) / 1000)))
        : 0;
      const result = await learningApi.submitSingleAnswer(
        question.public_id, answer, wrongMode ? undefined : (question.deck_id || deckId),
        legacyGrade, usedSeconds,
      );
      const serverAnswer = result.correct_answer == null ? question.answer : result.correct_answer;
      const hasServerScore = result.score !== undefined
        && result.score !== null
        && result.score !== ''
        && Number.isFinite(Number(result.score));
      const authoritativeCorrect = serverCorrectFlag(result);
      setCorrectAnswer(normalizeAnswer(serverAnswer));
      setServerExplanation(result.explanation || '');
      // 旧后端只返回 recorded，此时使用本地结果；新后端一律以服务端分数为准。
      setScore(hasServerScore ? Number(result.score) : legacyGrade.score);
      // 结论使用权威判题布尔值，不再从分数反推，避免新旧协议下全部显示"回答错误"。
      const finalCorrect = authoritativeCorrect ?? legacyGrade.isCorrect;
      setAnswerIsCorrect(finalCorrect);
      setSubmitted(true);
      setShowExplanation(true);
      // 套题模式：累积本次成绩（供结算页展示）
      if (!wrongMode && question.deck_id) {
        const key = `kc_deck_${question.deck_id}`;
        const answeredKey = `${key}_answered_${question.public_id}`;
        try {
          if (!sessionStorage.getItem(answeredKey)) {
            const raw = sessionStorage.getItem(key);
            if (raw) {
              const obj = JSON.parse(raw);
              obj.answered = (obj.answered || 0) + 1;
              // 深度知识题不计对错：单独计数，不进答对/答错分母
              const isDeepQ = question.type === 'deep' || !!question.is_deep;
              if (isDeepQ) obj.deep = (obj.deep || 0) + 1;
              else if (finalCorrect) obj.correct = (obj.correct || 0) + 1;
              sessionStorage.setItem(key, JSON.stringify(obj));
              sessionStorage.setItem(answeredKey, '1');
            }
          }
        } catch { /* ignore */ }
      }
    } catch (e) {
      setSubmitError(e instanceof Error ? e.message : '答案提交失败，请重试');
    } finally {
      setSubmitting(false);
    }
  }

  // 跳转到下一道错题
  function handleWrongNext() {
    const nextIdx = wrongIdx + 1;
    if (nextIdx < wrongIds.length) {
      navigate(`/question/${wrongIds[nextIdx]}?mode=wrong&ids=${wrongIds.join(',')}&idx=${nextIdx}`);
    } else {
      navigate('/wrong-book');
    }
  }

  // 跳转到套题下一题
  function handleDeckNext() {
    if (question?.next_public_id) {
      const contextDeckId = question.deck_id || deckId;
      navigate(`/question/${question.next_public_id}${contextDeckId ? `?deck_id=${encodeURIComponent(contextDeckId)}` : ''}`);
    }
  }

  // 套题完成：把累积成绩写入结算缓存，跳结算页
  function finishDeck() {
    if (question?.deck_id) {
      const key = `kc_deck_${question.deck_id}`;
      try {
        const raw = sessionStorage.getItem(key);
        if (raw) sessionStorage.setItem('kc_deck_result', raw);
        sessionStorage.removeItem(key);
      } catch { /* ignore */ }
    }
    navigate('/result/finish');
  }

  if (loading) {
    return (
      <div className="sq-loading">
        <div className="sq-spinner" />
        <p>题目加载中...</p>
      </div>
    );
  }

  if (error || !question) {
    const forbidden = !!error?.forbidden;
    return (
      <div className="sq-error">
        <div className="sq-error-icon">
          <Icon name={forbidden ? 'lock' : 'qrcode'} size={48} color={forbidden ? 'var(--color-warning, #f59e0b)' : 'var(--gray-300)'} />
        </div>
        <p className="sq-error-title">{forbidden ? '暂无答题权限' : '未找到这道题'}</p>
        <p className="sq-error-msg">
          {forbidden
            ? '该题为企业定制专属题，仅授权企业的学员可扫码学习。如您属于该企业，请联系企业管理员开通答题权限。'
            : '该二维码对应的题目可能已更新或暂未上架，请确认扫码的是最新版知识卡；如多次出现，请联系管理员补录题目。'}
        </p>
        <div className="sq-error-actions">
          <button className="sq-btn-primary sq-btn-lg" onClick={() => window.location.reload()}>重新加载</button>
          <button className="sq-btn-ghost" onClick={() => navigate('/')}>返回首页</button>
        </div>
      </div>
    );
  }

  const isDeep = question.type === 'deep' || question.is_deep;
  // deep题如果有选项，按单选题正常作答；无选项才作为案例题直接查看解析
  const hasOptions = Array.isArray(question.options) && question.options.length > 0;
  const deepAsCase = isDeep && !hasOptions;
  const typeColor = TYPE_COLOR[question.type] || '#3b82f6';
  const isCorrect = submitted && answerIsCorrect === true;
  const isPartial = submitted
    && answerIsCorrect === false
    && question.type === 'multi'
    && score > 0;
  const optionLetters = ['A', 'B', 'C', 'D', 'E', 'F'];

  // 套题信息
  const inDeck = !wrongMode && !!question.deck_id;
  const deckPos = question.position ?? 0;
  const deckTotal = question.total_in_deck ?? 0;
  const hasDeckNext = !!question.next_public_id;

  // 错题本模式信息
  const wrongHasNext = wrongMode && wrongIdx < wrongIds.length - 1;

  return (
    <div className="sq-page">
      {/* 顶部进度条 */}
      <div className="sq-header">
        <button className="sq-close" onClick={() => navigate(wrongMode ? '/wrong-book' : '/')}>
          <Icon name="close" size={20} />
        </button>
        <div className="sq-header-title">
          <span className="sq-type-dot" style={{ background: typeColor }} />
          {TYPE_LABEL[question.type] || question.type}
        </div>
        <div className="sq-card-no">
          {inDeck && deckPos > 0
            ? `第${deckPos}题`
            : `#${publicId?.slice(-4) || cardNo?.slice(0, 6) || '?'}`}
        </div>
      </div>

      {/* 套题进度 / 错题本进度 */}
      {wrongMode && wrongIds.length > 0 && (
        <div className="sq-deck-bar" style={{ background: '#fef3c7', borderColor: '#fbbf24' }}>
          <span className="sq-deck-title" style={{ color: '#92400e' }}>
            <Icon name="refresh" size={14} /> 错题连续练习
          </span>
          <span className="sq-deck-progress" style={{ color: '#92400e' }}>
            第 {wrongIdx + 1} 题 / 共 {wrongIds.length} 题
          </span>
        </div>
      )}
      {inDeck && (
        <div className="sq-deck-bar">
          <span className="sq-deck-title">
            <Icon name="book-open" size={14} /> {question.deck_title || '套题练习'}
          </span>
          <span className="sq-deck-progress">
            第 {deckPos} 题 / 共 {deckTotal} 题
          </span>
        </div>
      )}
      {/* 过期卡提示：已过期但已定位到当前版本题目/相似题（需求书 B4 到期失效可感知） */}
      {question.notice && (
        <div className="sq-deck-bar" style={{ background: '#fef2f2', borderColor: '#fca5a5' }}>
          <span className="sq-deck-title" style={{ color: '#991b1b' }}>
            <Icon name="alert-circle" size={14} /> {question.notice}
          </span>
        </div>
      )}

      {/* 题目卡片 */}
      <div className="sq-question-card">
        <div className="sq-question-meta">
          <span className="sq-tag" style={{ background: `${typeColor}15`, color: typeColor }}>
            {TYPE_LABEL[question.type] || question.type}
          </span>
          {question.difficulty && (
            <span className="sq-difficulty" title={`难度 ${question.difficulty}/5`}>
              {Array.from({ length: 5 }).map((_, i) => (
                <Icon key={i} name="star-filled" size={12}
                  color={i < question.difficulty! ? '#f59e0b' : '#d1d5db'} />
              ))}
            </span>
          )}
        </div>

        <div className="sq-stem">
          {renderStem(question.stem)}
        </div>

        {deepAsCase && !submitted ? (
          <div className="sq-deep-hint">
            <span className="sq-deep-icon"><Icon name="lightbulb" size={20} color="#f59e0b" /></span>
            <p>这是一道深度知识题，点击下方按钮查看完整专业解析。</p>
          </div>
        ) : (
          <div className="sq-options">
            {(question.options || []).map((opt, i) => {
              const isSel = selected.includes(i);
              let cls = 'sq-option';
              if (submitted) {
                if (isCorrectOption(i)) cls += ' correct';
                else if (isSel) cls += ' wrong';
              } else if (isSel) {
                cls += ' selected';
              }
              return (
                <div key={i} className={cls} onClick={() => toggleOption(i)}>
                  <div className={`sq-option-letter ${isSel ? 'selected' : ''} ${submitted && isCorrectOption(i) ? 'correct' : ''} ${submitted && isSel && !isCorrectOption(i) ? 'wrong' : ''}`}>
                    {submitted && isCorrectOption(i)
                      ? <Icon name="check" size={14} color="#fff" />
                      : submitted && isSel && !isCorrectOption(i)
                        ? <Icon name="close" size={14} color="#fff" />
                        : optionLetters[i]}
                  </div>
                  <div className="sq-option-text">{opt}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* 答题结果反馈 */}
        {submitted && showExplanation && !deepAsCase && (
          <div className={`sq-feedback ${isCorrect ? 'correct' : isPartial ? 'partial' : 'wrong'}`}>
            <div className="sq-feedback-header">
              <span className="sq-feedback-icon">
                {isCorrect ? <Icon name="check-circle" size={28} color="#059669" filled />
                  : isPartial ? <Icon name="alert-circle" size={28} color="#d97706" filled />
                  : <Icon name="close-circle" size={28} color="#dc2626" filled />}
              </span>
              <div>
                <div className="sq-feedback-title">
                  {isCorrect ? '回答正确！' : isPartial ? '部分正确' : '回答错误'}
                </div>
                <div className="sq-feedback-sub">
                  {isCorrect ? '太棒了，继续加油！' : isPartial ? '少选可得一半分数，再接再厉' : '别灰心，看看解析吧'}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 解析（普通题与深度题统一显示，深度题用不同标题色） */}
        {submitted && (
          <div className="sq-explanation">
            <div className="sq-explanation-header">
              <Icon name="book-open" size={16} color={isDeep ? 'var(--color-warning)' : 'var(--color-primary)'} />
              <span>{isDeep ? '深度解析' : '专业解析'}</span>
            </div>
            <div className="sq-explanation-body" style={{ whiteSpace: 'pre-wrap' }}>
              {(serverExplanation || question.explanation) && (serverExplanation || question.explanation).trim()
                ? (serverExplanation || question.explanation)
                : (question.stem && question.stem.replace(/\[(?:IMG|VIDEO):[^\]]+\]\s*/g, '').trim()
                    ? '本题为专业知识题，请结合题目描述与实际工作场景理解。正确答案已标出，建议将错题加入错题本反复练习。'
                    : '暂无解析，建议将本题加入错题本后续复习。')}
            </div>
          </div>
        )}
      </div>

      {/* 底部操作按钮 */}
      <div className="sq-footer">
        {submitError && <div className="auth-error" style={{ marginBottom: 8 }}>{submitError}</div>}
        {!submitted ? (
          deepAsCase ? (
            <button className="sq-btn-primary sq-btn-lg" onClick={() => handleSubmit([])} disabled={submitting}>
              {submitting ? '提交中...' : '查看深度解析'}
            </button>
          ) : (
            <button
              className={`sq-btn-primary sq-btn-lg ${selected.length === 0 ? 'disabled' : ''}`}
              onClick={() => handleSubmit()}
              disabled={selected.length === 0 || submitting}
            >
              {submitting ? '提交中...' : selected.length === 0
                ? '请选择答案'
                : question.type === 'multi'
                  ? `提交答案（已选${selected.length}项）`
                  : '提交答案'}
            </button>
          )
        ) : wrongMode ? (
          /* 错题本连续练习模式 */
          <div className="sq-footer-actions">
            <button className="sq-btn-ghost" onClick={() => navigate('/wrong-book')}>
              <Icon name="book" size={16} /> 错题本
            </button>
            {wrongHasNext ? (
              <button className="sq-btn-primary sq-btn-lg" onClick={handleWrongNext}>
                下一道错题 ({wrongIdx + 2}/{wrongIds.length}) <Icon name="arrow-right" size={16} color="#fff" />
              </button>
            ) : (
              <button className="sq-btn-primary sq-btn-lg" onClick={() => navigate('/wrong-book')}>
                <Icon name="check" size={16} color="#fff" /> 完成练习
              </button>
            )}
          </div>
        ) : inDeck && hasDeckNext ? (
          /* 套题模式：有下一题 */
          <div className="sq-footer-actions">
            <button className="sq-btn-ghost" onClick={() => navigate('/wrong-book')}>
              <Icon name="book" size={16} /> 错题本
            </button>
            <button className="sq-btn-primary sq-btn-lg" onClick={handleDeckNext}>
              下一题 <Icon name="arrow-right" size={16} color="#fff" />
            </button>
          </div>
        ) : inDeck && !hasDeckNext ? (
          /* 套题最后一题 */
          <div className="sq-footer-actions">
            <button className="sq-btn-ghost" onClick={() => navigate('/wrong-book')}>
              <Icon name="book" size={16} /> 错题本
            </button>
            <button className="sq-btn-primary sq-btn-lg" onClick={finishDeck}>
              <Icon name="check" size={16} color="#fff" /> 完成套题
            </button>
          </div>
        ) : (
          /* 普通单题模式 */
          <div className="sq-footer-actions">
            <button className="sq-btn-ghost" onClick={() => navigate('/wrong-book')}>
              <Icon name="book" size={16} /> 错题本
            </button>
            <button className="sq-btn-primary sq-btn-lg" onClick={() => navigate('/')}>
              完成
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// AI 导入的题干可能使用 markdown 图片 ![IMG](url)；统一转成系统标准 [IMG:url]
function normalizeStemMedia(stem: string): string {
  return (stem || '').replace(
    /!\[[^\]]*\]\(\s*([^)\s]+)(?:\s+["'][^"']*["'])?\s*\)/g,
    (_m, url: string) => `[IMG:${String(url).trim()}]`,
  );
}

function renderStem(stem: string): React.ReactNode {
  // 兼容图片 [IMG:url] 与视频 [VIDEO:url] 标记，捕获组 1=图片，捕获组 2=视频
  const parts = normalizeStemMedia(stem).split(/\[IMG:([^\]]+)\]|\[VIDEO:([^\]]+)\]/g);
  const nodes: React.ReactNode[] = [];
  for (let i = 0; i < parts.length; i += 3) {
    if (parts[i]) nodes.push(<span key={i}>{parts[i]}</span>);
    const imgUrl = parts[i + 1];
    const videoUrl = parts[i + 2];
    if (imgUrl) {
      nodes.push(
        <img
          key={`img${i}`}
          src={imgUrl}
          alt="题目配图"
          className="sq-stem-img"
          loading="lazy"
          onError={(e) => {
            const el = e.currentTarget;
            el.style.display = 'none';
            const ph = el.nextElementSibling as HTMLElement | null;
            if (ph && ph.classList.contains('sq-stem-img-ph')) ph.style.display = 'inline';
          }}
        />
      );
    }
    if (videoUrl) {
      nodes.push(
        <video
          key={`video${i}`}
          className="sq-stem-video"
          src={videoUrl}
          controls
          playsInline
          preload="none"
        />
      );
    }
  }
  return nodes;
}
